import React, { useState } from 'react';
import WithSection from '../../components/WithSection/WithSection';
import {
    Body1,
    Caption,
    Head3,
    Head4,
    Head6,
    Headline,
} from '../../components/Typography/Typography';
import Box from '../../components/Box/Box';
import { rem } from 'polished';
import { PostCard } from '../../components/PostCard/PostCard';
import { graphql } from 'gatsby';
import Grid, { gridGap, gridInlinePadding } from '../../components/Grid/Grid';
import Field from '../../components/Field/Field';
import { EMAIL_REGEX, trackedEvents } from '../../../config/constants';
import Button from '../../components/Button/Button';
import { useForm } from 'react-hook-form';
import { subscribeToOurBlog } from '../../services/blog';
import { SubscribeToBlogSuccessMessage } from '../../components/SubscribeToBlog/SubscribeToBlog';
import { track } from '../../utils/analytics';
import RichText from '../../components/RichText/RichText';

const FeaturedPostsSection = ({ data }) => {
    const {
        items,
        primary: { title, show_newsletter_box, featured_posts_section_heading },
    } = data;
    const featuredPosts = items.map((item) => item.post.document);
    const { register, errors, handleSubmit } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleOnSubmit = (data) => {
        setIsLoading(true);
        subscribeToOurBlog(data)
            .then(() => {
                setIsSubmitted(true);
                track(trackedEvents.subscribeToBlog);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            {show_newsletter_box ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    gridColumn={{
                        _: '1/-1',
                        sm: '1/ span 3',
                        lg: '2/ span 3',
                    }}
                    mb={{ _: rem(24), sm: 0 }}
                >
                    <Head4 mb={rem(16)}>Welcome to the adam.ai blog</Head4>
                    <Box
                        borderRadius="sm"
                        boxShadow="primary-thin"
                        p={rem(16)}
                        flex={1}
                    >
                        {isSubmitted ? (
                            <SubscribeToBlogSuccessMessage isInsideBox />
                        ) : (
                            <>
                                <Head6 mb={rem(8)}>Subscribe to our blog</Head6>
                                <Body1 mb={rem(16)}>
                                    Get the latest blog posts sent straight to
                                    your inbox:
                                </Body1>
                                <Box
                                    as="form"
                                    onSubmit={handleSubmit(handleOnSubmit)}
                                >
                                    <Field id="email" width="100%">
                                        <Field.Label>Email address</Field.Label>
                                        <Field.Input
                                            type="email"
                                            placeholder="name@internet.cool"
                                            ref={register({
                                                required: 'Email is required',
                                                pattern: {
                                                    value: EMAIL_REGEX,
                                                    message: 'Invalid Email',
                                                },
                                            })}
                                            error={errors.email}
                                        />
                                        {errors.email && (
                                            <Field.ErrorMessage>
                                                {errors.email.message}
                                            </Field.ErrorMessage>
                                        )}
                                    </Field>
                                    <Box py={rem(8)}>
                                        <Button
                                            type="submit"
                                            size="lg"
                                            loading={isLoading}
                                            fullWidth
                                            mt={rem(16)}
                                        >
                                            Subscribe
                                        </Button>
                                    </Box>
                                    <Caption>
                                        You can unsubscribe at anytime
                                    </Caption>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            ) : null}

            <Box
                gridColumn={{
                    _: '1/-1',
                    ...(show_newsletter_box
                        ? { sm: '4/ -1', lg: '5/ -2' }
                        : { lg: '2/-2' }),
                }}
            >
                {featured_posts_section_heading.richText && (
                    <Box>
                        <RichText
                            render={featured_posts_section_heading.richText}
                        />
                    </Box>
                )}
                {title && (
                    <Box mb={rem(16)}>
                        <Headline>{title}</Headline>
                    </Box>
                )}
                <Box
                    display="grid"
                    gridTemplateColumns={{
                        _: '1fr',
                        ...(show_newsletter_box
                            ? {
                                  md: '1fr 1fr',
                                  lg: '1.5fr 1fr',
                              }
                            : { sm: '1fr 1fr', lg: '1fr 1fr 1fr' }),
                    }}
                    gridGap={gridGap}
                >
                    <PostCard post={featuredPosts[0]} isFeaturedPosts />
                    <PostCard
                        post={featuredPosts[1]}
                        isFeaturedPosts
                        {...(show_newsletter_box && {
                            display: { _: 'none', md: 'block' },
                        })}
                    />
                    {featuredPosts[2] && !show_newsletter_box ? (
                        <PostCard post={featuredPosts[2]} isFeaturedPosts />
                    ) : null}
                </Box>
            </Box>
        </>
    );
};

export default WithSection(FeaturedPostsSection);

export const _ = graphql`
    fragment FEATURED_POSTS on PrismicSectionDataBodyFeaturedPosts {
        id
        primary {
            featured_posts_section_heading {
                richText
            }
            title
            show_newsletter_box
        }
        items {
            post {
                document {
                    ... on PrismicPost {
                        uid
                        data {
                            date_published
                            image {
                                alt
                                gatsbyImageData(layout: CONSTRAINED)
                            }
                            title
                            tags {
                                tag {
                                    document {
                                        ... on PrismicTag {
                                            data {
                                                name
                                            }
                                        }
                                    }
                                }
                            }
                            ...POST_BODY_FRAGMENT
                        }
                    }
                }
            }
        }
    }
`;
